import { Injectable, signal } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Subject } from 'rxjs';

const STATS_POLL_INTERVAL_MS = 1_000;
const STATS_LOG_EVERY_NTH_SAMPLE = 5;
// A connected peer that has not decoded a single frame by now is not "slow", it is silent:
// ICE and DTLS complete in well under a second on the device AP.
const VIDEO_TIMEOUT_MS = 10_000;
// The candidate queue is flushed before the answer is applied, so an unbounded PATCH would stall
// the whole connect - with no error and no video timeout, because "connected" is never reached.
const WHEP_PATCH_TIMEOUT_MS = 10_000;

/**
 * Android's CapacitorHttp hands us the response body with every CR stripped and the final line
 * terminator dropped (813 bytes on the wire arrive as 785). libwebrtc's SDP parser scans for the
 * next newline, so the unterminated last line fails as "Invalid SDP line" — naming whichever
 * attribute happens to be last, which is why this reads like an ICE problem. iOS delivers the body
 * untouched, hence Android-only.
 */
function terminateSdp(sdp: string): string {
  return sdp.endsWith('\n') ? sdp : `${sdp}\r\n`;
}

/** Live receive-side metrics of the video track, sampled from RTCPeerConnection.getStats(). */
export interface WebrtcStreamStats {
  /** Source resolution as the device actually encodes it. */
  frameWidth: number;
  frameHeight: number;
  framesPerSecond: number;
  /** framesReceived vs framesDecoded separates a reassembly problem from a decoder that lags. */
  framesReceived: number;
  framesDecoded: number;
  framesDropped: number;
  keyFramesDecoded: number;
  packetsReceived: number;
  packetsLost: number;
  jitterMs: number;
  freezeCount: number;
  totalFreezesDurationMs: number;
  /** Retransmission and keyframe requests we send — they stay at 0 if the sender ignores them. */
  nackCount: number;
  pliCount: number;
  /** Derived from the byte delta between two samples. */
  receiveBitrateKbps: number;
}

/** getStats() reports are loosely typed across engines — read every field defensively. */
interface InboundRtpReport {
  type: string;
  kind?: string;
  frameWidth?: number;
  frameHeight?: number;
  framesPerSecond?: number;
  framesReceived?: number;
  framesDecoded?: number;
  framesDropped?: number;
  keyFramesDecoded?: number;
  packetsReceived?: number;
  packetsLost?: number;
  jitter?: number;
  freezeCount?: number;
  totalFreezesDuration?: number;
  nackCount?: number;
  pliCount?: number;
  bytesReceived?: number;
  timestamp?: number;
}

/**
 * Manages the client RTCPeerConnection for receiving a WebRTC stream.
 *
 * Two sources: a WHEP endpoint (the Cannes device on its AP network, or a public demo stream) and
 * an in-page loopback connection driven by a caller-provided connect callback (the test page's mock).
 */
@Injectable({ providedIn: 'root' })
export class WebrtcStreamService {
  // private state
  private readonly connectionStateSubject = new Subject<string>();
  private readonly remoteStreamSignal = signal<MediaStream | null>(null);
  private readonly logsSignal = signal<Array<string>>([]);
  private peerConnection?: RTCPeerConnection;
  private whepResourceUrl?: string;
  private whepCandidateQueue: Array<string> = [];
  private remoteStream?: MediaStream;
  private readonly statsSignal = signal<WebrtcStreamStats | null>(null);
  private readonly statsEnabledSignal = signal(false);
  private readonly videoTimeoutSubject = new Subject<void>();
  private statsTimer?: ReturnType<typeof setInterval>;
  private videoTimeoutTimer?: ReturnType<typeof setTimeout>;
  private lastStatsSample?: { bytesReceived: number; timestamp: number };
  private statsSampleCount = 0;

  // public state

  /** Connection state changes of the active client connection. */
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  /**
   * Fires when the peer connection is established but the device never delivered a decodable
   * frame. Without this the failure is invisible: the connection reports success and the video
   * element simply stays black.
   */
  readonly videoTimeout$ = this.videoTimeoutSubject.asObservable();

  /** Media stream received from the current connection. */
  readonly remoteStream$$ = this.remoteStreamSignal.asReadonly();

  /**
   * Diagnostic log of the current run: when the phone joins the isolated device AP,
   * the Mac loses its debug connection and the Xcode console goes dark — on-page
   * logs are the only way to see what happens.
   */
  readonly logs$$ = this.logsSignal.asReadonly();

  /**
   * Live receive-side metrics, sampled once per second while connected. Only collected in builds
   * with the test section enabled, and only while the diagnostics toggle is on.
   */
  readonly stats$$ = this.statsSignal.asReadonly();

  /** Whether the stream diagnostics are switched on (test section toggle). */
  readonly isStatsEnabled$$ = this.statsEnabledSignal.asReadonly();

  /**
   * Turns the receive-side diagnostics on or off. Takes effect on a running stream too, so the
   * toggle can be flipped while watching rather than only before starting.
   */
  public setStatsEnabled(enabled: boolean): void {
    this.statsEnabledSignal.set(enabled);
    if (!FEATURE_TEST_SECTION) {
      return;
    }
    if (enabled && this.peerConnection?.connectionState === 'connected') {
      this.startStatsPolling(this.peerConnection);
      return;
    }
    if (!enabled) {
      this.stopStatsPolling();
      this.statsSignal.set(null);
    }
  }

  /** Streams through an in-page loopback connection wired up by the caller. */
  public async startLoopback$(connect: (clientConnection: RTCPeerConnection) => Promise<void>): Promise<void> {
    if (this.peerConnection) {
      this.connectionStateSubject.next(this.peerConnection.connectionState);
      return;
    }
    this.logsSignal.set([]);
    const clientConnection = this.createClientConnection();
    try {
      await connect(clientConnection);
    } catch (error) {
      this.closeConnection();
      throw error;
    }
  }

  /** Subscribes to a remote stream via the WHEP protocol. */
  public async startOnline$(endpoint: string, useStun = true): Promise<void> {
    if (this.peerConnection) {
      this.connectionStateSubject.next(this.peerConnection.connectionState);
      return;
    }
    this.logsSignal.set([]);
    // Public streams need STUN for server-reflexive candidates; the Cannes AP network is
    // isolated (no internet), so the STUN server is unreachable there — host candidates only.
    const iceServers: Array<RTCIceServer> = useStun ? [{ urls: 'stun:stun.l.google.com:19302' }] : [];
    const clientConnection = this.createClientConnection(iceServers);
    try {
      await this.connectViaWhep$(endpoint, clientConnection);
    } catch (error) {
      this.log(`failed: ${error instanceof Error ? error.message : String(error)}`);
      this.closeConnection();
      throw error;
    }
  }

  /** Releases the WHEP resource (if any) and closes the connection. */
  public stop(): void {
    if (this.whepResourceUrl) {
      const resourceUrl = this.whepResourceUrl;
      // A silently swallowed DELETE leaves the session alive on the device: it keeps sending and
      // the next run competes with it for the same link. The device has been seen answering
      // slowly, so give the teardown the same headroom as the offer and log how it went.
      const deleted = Capacitor.isNativePlatform()
        ? CapacitorHttp.delete({ url: resourceUrl, connectTimeout: 30_000, readTimeout: 30_000 }).then(response => `status ${response.status}`)
        : fetch(resourceUrl, { method: 'DELETE' }).then(response => `status ${response.status}`);
      void deleted
        .catch((error: unknown) => `failed: ${error instanceof Error ? error.message : String(error)}`)
        .then(outcome => this.log(`WHEP DELETE ${resourceUrl} - ${outcome}`));
      this.whepResourceUrl = undefined;
    }
    this.closeConnection();
  }

  /** WHEP signaling (Cannes device and public demo streams): POST the offer, apply the SDP answer. */
  private async connectViaWhep$(endpoint: string, pc: RTCPeerConnection): Promise<void> {
    pc.addTransceiver('video', { direction: 'recvonly' });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await this.waitForIceGathering$(pc);

    this.log(`sending WHEP offer to ${endpoint}`);
    const { status, location, body } = await this.postWhepOffer$(endpoint, pc.localDescription?.sdp ?? '');
    this.log(`WHEP response: ${status} (location: ${location ?? 'none'})`);
    if (status < 200 || status >= 300) {
      throw new Error(`WHEP request failed: ${status} - ${body.slice(0, 200)}`);
    }
    this.whepResourceUrl = location ? new URL(location, endpoint).toString() : undefined;
    await this.flushWhepCandidateQueue();

    this.logAnswerVideoCodec(body);
    await pc.setRemoteDescription({ type: 'answer', sdp: terminateSdp(body) });
    this.log('remote answer applied - waiting for ICE');
  }

  /**
   * Sends the WHEP offer, preferring the native HTTP layer on device (it is subject to
   * neither CORS nor mixed-content rules) and falling back to the WebView fetch.
   */
  private async postWhepOffer$(endpoint: string, sdp: string): Promise<{ status: number; location: string | null; body: string }> {
    this.log(`offer SDP length: ${sdp.length}`);

    if (Capacitor.isNativePlatform()) {
      try {
        const response = await CapacitorHttp.post({
          url: endpoint,
          headers: { 'Content-Type': 'application/sdp' },
          data: sdp,
          // Without this the native layer tries to parse the SDP answer as JSON.
          responseType: 'text',
          // The device has been seen taking >10 s to answer the POST. A timeout here is not a
          // failure we can retry safely: the session is created before the answer comes back, so
          // the retry leaves an orphaned session behind that nothing ever DELETEs.
          connectTimeout: 30_000,
          readTimeout: 30_000,
        });
        const location = response.headers['Location'] ?? response.headers['location'] ?? null;
        return { status: response.status, location, body: typeof response.data === 'string' ? response.data : String(response.data) };
      } catch (error) {
        this.log(`native POST failed: ${error instanceof Error ? error.message : String(error)} - retrying via WebView`);
      }
    }

    return this.fetchWhepOffer$(endpoint, sdp);
  }

  /**
   * Trickle-ICE: hands one local candidate to the device via PATCH. Candidates found before the
   * POST returns have no session URL yet, so they are queued and flushed once it arrives.
   *
   * We already send every candidate inside the offer, so this is redundant by the protocol — but
   * the device answers as soon as its ICE credentials exist, before its own candidates are
   * gathered, and WHEP gives it no way to send them afterwards. Trickling ours lets the device
   * reach us instead, which is how its own browser player connects at all.
   */
  private async sendWhepIceCandidate(candidate: string): Promise<void> {
    if (!this.whepResourceUrl) {
      this.whepCandidateQueue.push(candidate);
      return;
    }

    const url = this.whepResourceUrl;
    const headers = { 'Content-Type': 'application/trickle-ice-sdpfrag' };
    const sdpFragment = `a=${candidate}\r\n`;
    const abortController = new AbortController();
    const patchTimeout = setTimeout(() => abortController.abort(), WHEP_PATCH_TIMEOUT_MS);
    try {
      const status = Capacitor.isNativePlatform()
        ? (
            await CapacitorHttp.patch({
              url,
              headers,
              data: sdpFragment,
              responseType: 'text',
              connectTimeout: WHEP_PATCH_TIMEOUT_MS,
              readTimeout: WHEP_PATCH_TIMEOUT_MS,
            })
          ).status
        : (await fetch(url, { method: 'PATCH', headers, body: sdpFragment, signal: abortController.signal })).status;
      this.log(`WHEP PATCH candidate - status ${status}`);
    } catch (error) {
      // Not fatal: the candidates in the offer remain valid, this only adds a second delivery path.
      this.log(`WHEP PATCH failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      clearTimeout(patchTimeout);
    }
  }

  private async flushWhepCandidateQueue(): Promise<void> {
    const queued = this.whepCandidateQueue;
    this.whepCandidateQueue = [];
    for (const candidate of queued) {
      await this.sendWhepIceCandidate(candidate);
    }
  }

  private async fetchWhepOffer$(endpoint: string, sdp: string): Promise<{ status: number; location: string | null; body: string }> {
    // An unreachable endpoint hangs the fetch indefinitely — abort so the failure becomes visible.
    const abortController = new AbortController();
    const fetchTimeout = setTimeout(() => abortController.abort(), 10_000);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: sdp,
        signal: abortController.signal,
      });
      return { status: response.status, location: response.headers.get('Location'), body: await response.text() };
    } catch (error) {
      throw abortController.signal.aborted ? new Error(`WHEP endpoint not reachable within 10 s: ${endpoint}`) : error;
    } finally {
      clearTimeout(fetchTimeout);
    }
  }

  private createClientConnection(iceServers: Array<RTCIceServer> = []): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers });
    this.peerConnection = pc;

    pc.ontrack = event => {
      this.log(`track received: ${event.track.kind} (streams: ${event.streams.length})`);
      // One ontrack fires per m-line, and the tracks may carry different stream associations
      // (or none at all, when the SDP has no msid). Collecting them into one MediaStream keeps
      // a later audio track from replacing the video the element is already rendering.
      const stream = this.remoteStream ?? new MediaStream();
      this.remoteStream = stream;
      stream.addTrack(event.track);
      this.remoteStreamSignal.set(stream);
    };
    pc.onconnectionstatechange = () => {
      this.log(`connectionState: ${pc.connectionState}`);
      this.connectionStateSubject.next(pc.connectionState);
      if (pc.connectionState === 'connected') {
        this.scheduleVideoTimeoutCheck(pc);
        if (FEATURE_TEST_SECTION && this.statsEnabledSignal()) {
          this.startStatsPolling(pc);
        }
      }
    };
    pc.onicecandidate = event => {
      this.log(`local candidate: ${event.candidate?.candidate ?? '(gathering done)'}`);
      if (event.candidate) {
        void this.sendWhepIceCandidate(event.candidate.candidate);
      }
    };
    pc.oniceconnectionstatechange = () => this.log(`iceConnectionState: ${pc.iceConnectionState}`);
    pc.onicegatheringstatechange = () => this.log(`iceGatheringState: ${pc.iceGatheringState}`);

    return pc;
  }

  /**
   * The H.264 profile-level-id the sender commits to says which resolution and bitrate class it
   * intends to deliver — the decisive number when a stream plays on a desktop client but not on
   * the phone. Test builds only; it is a bring-up diagnostic, not product behaviour.
   */
  private logAnswerVideoCodec(sdp: string): void {
    if (!FEATURE_TEST_SECTION || !this.statsEnabledSignal()) {
      return;
    }
    for (const line of sdp.split(/\r?\n/)) {
      if (line.startsWith('m=video') || (line.startsWith('a=fmtp:') && line.includes('profile-level-id')) || line.startsWith('a=rtcp-fb:')) {
        this.log(`answer: ${line}`);
      }
    }
  }

  private scheduleVideoTimeoutCheck(pc: RTCPeerConnection): void {
    clearTimeout(this.videoTimeoutTimer);
    this.videoTimeoutTimer = setTimeout(() => {
      if (this.peerConnection !== pc) {
        return;
      }
      void pc.getStats().then(report => {
        // getStats resolves asynchronously: the stream can be stopped while the query is in
        // flight, and reporting then would put a closed stream into the error state — or worse,
        // fail a fresh attempt the user has meanwhile started.
        if (this.peerConnection !== pc || this.findInboundVideo(report)?.framesDecoded) {
          return;
        }
        this.log(`no decodable video within ${VIDEO_TIMEOUT_MS / 1000} s`);
        this.videoTimeoutSubject.next();
      });
    }, VIDEO_TIMEOUT_MS);
  }

  private findInboundVideo(report: RTCStatsReport): InboundRtpReport | undefined {
    let video: InboundRtpReport | undefined;
    report.forEach(entry => {
      const candidate = entry as InboundRtpReport;
      if (candidate.type === 'inbound-rtp' && candidate.kind === 'video') {
        video = candidate;
      }
    });
    return video;
  }

  private startStatsPolling(pc: RTCPeerConnection): void {
    this.stopStatsPolling();
    this.statsTimer = setInterval(() => {
      if (this.peerConnection !== pc) {
        this.stopStatsPolling();
        return;
      }
      void pc.getStats().then(report => {
        if (this.peerConnection !== pc) {
          return;
        }
        const stats = this.toStreamStats(report);
        this.statsSignal.set(stats);
        // The live-stream modal has no stats panel — mirror a sample into the log every few
        // seconds so a run from that surface still yields numbers in ios.log. A missing report
        // is reported explicitly: the engine only creates one once the first RTP packet lands,
        // so silence here means "nothing arrived", which is a finding rather than an absence.
        if (++this.statsSampleCount % STATS_LOG_EVERY_NTH_SAMPLE !== 0) {
          return;
        }
        this.log(
          stats
            ? `stats: ${stats.frameWidth}x${stats.frameHeight} ${stats.framesPerSecond}fps ${stats.receiveBitrateKbps}kbit/s ` +
                `frames rx/dec/drop=${stats.framesReceived}/${stats.framesDecoded}/${stats.framesDropped} key=${stats.keyFramesDecoded} ` +
                `pkt rx/lost=${stats.packetsReceived}/${stats.packetsLost} jitter=${stats.jitterMs}ms freeze=${stats.freezeCount} ` +
                `nack/pli=${stats.nackCount}/${stats.pliCount}`
            : 'stats: no inbound-rtp video report - no RTP was decrypted into the receiver'
        );
        // Transport counts raw bytes on the wire, before RTP parsing and SRTP decryption. Bytes
        // here with no inbound-rtp report above means the device IS sending and we are discarding
        // it locally; zero here means nothing reaches the phone at all.
        this.log(`transport: ${this.toTransportSummary(report)}`);
      });
    }, STATS_POLL_INTERVAL_MS);
  }

  private stopStatsPolling(): void {
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = undefined;
    }
    this.lastStatsSample = undefined;
    this.statsSampleCount = 0;
  }

  private toTransportSummary(report: RTCStatsReport): string {
    const parts: Array<string> = [];
    report.forEach(entry => {
      const candidate = entry as { type: string; state?: string; bytesReceived?: number; bytesSent?: number };
      if (candidate.type === 'transport' || (candidate.type === 'candidate-pair' && candidate.state === 'succeeded')) {
        parts.push(`${candidate.type} rx=${candidate.bytesReceived ?? 0} tx=${candidate.bytesSent ?? 0}`);
      }
    });
    return parts.length > 0 ? parts.join(' | ') : 'no transport/candidate-pair report';
  }

  private toStreamStats(report: RTCStatsReport): WebrtcStreamStats | null {
    const video = this.findInboundVideo(report);
    if (!video) {
      return null;
    }

    const bytesReceived = video.bytesReceived ?? 0;
    const timestamp = video.timestamp ?? 0;
    const previous = this.lastStatsSample;
    // The first sample has nothing to compare against, so the rate stays 0 until the second one.
    const elapsedMs = previous ? timestamp - previous.timestamp : 0;
    const receiveBitrateKbps = elapsedMs > 0 ? Math.round(((bytesReceived - previous!.bytesReceived) * 8) / elapsedMs) : 0;
    this.lastStatsSample = { bytesReceived, timestamp };

    return {
      frameWidth: video.frameWidth ?? 0,
      frameHeight: video.frameHeight ?? 0,
      framesPerSecond: video.framesPerSecond ?? 0,
      framesReceived: video.framesReceived ?? 0,
      framesDecoded: video.framesDecoded ?? 0,
      framesDropped: video.framesDropped ?? 0,
      keyFramesDecoded: video.keyFramesDecoded ?? 0,
      packetsReceived: video.packetsReceived ?? 0,
      packetsLost: video.packetsLost ?? 0,
      jitterMs: Math.round((video.jitter ?? 0) * 1000),
      freezeCount: video.freezeCount ?? 0,
      totalFreezesDurationMs: Math.round((video.totalFreezesDuration ?? 0) * 1000),
      nackCount: video.nackCount ?? 0,
      pliCount: video.pliCount ?? 0,
      receiveBitrateKbps,
    };
  }

  private log(message: string): void {
    this.logsSignal.update(logs => [...logs.slice(-49), message]);
    // The live-stream modal renders no log panel, so on device these diagnostics are otherwise
    // invisible. Mirroring them to the console puts them in ios.log — test builds only.
    if (FEATURE_TEST_SECTION) {
      console.error(`[webrtc] ${message}`);
    }
  }

  private closeConnection(): void {
    clearTimeout(this.videoTimeoutTimer);
    this.videoTimeoutTimer = undefined;
    this.stopStatsPolling();
    this.statsSignal.set(null);
    this.peerConnection?.close();
    this.peerConnection = undefined;
    this.whepCandidateQueue = [];
    // Without this a failed connect leaves the old session URL behind, and the next connection -
    // including a loopback one - would trickle its candidates to that dead session.
    this.whepResourceUrl = undefined;
    this.remoteStream = undefined;
    this.remoteStreamSignal.set(null);
  }

  // 5 s to match the device's own player; anything still missing at that point is trickled via PATCH.
  private waitForIceGathering$(pc: RTCPeerConnection, timeoutMs = 5000): Promise<void> {
    if (pc.iceGatheringState === 'complete') {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      const timeout = setTimeout(resolve, timeoutMs);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        }
      });
    });
  }
}
